import React, { useState, useEffect } from 'react';
import {useLocation, useNavigate} from 'react-router-dom';import axios from 'axios';
import {useTranslation} from 'react-i18next';import {useTheme} from '@/contexts/ThemeContext';import {API_URL as API} from '@/utils/apiConfig';
import { isRegisteredUser } from '@/utils/authState';
export default function ProtectedRoute({ children }) {
  const { t, i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [slow, setSlow] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { changeTheme, changeFontSize, changeFontFamily, changeColorMode } = useTheme();

  useEffect(() => {
    // If user data passed from AuthGate via location state, skip auth check entirely
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      applyUserSettings();
      return;
    }

    // Only run auth check once (on mount), not on every location change
    if (isAuthenticated !== null) return;

    let cancelled = false;

    // replace (not push) so a /login entry never lingers in back-history.
    const toLogin = () => {
      const returnTo = location.pathname + location.search;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    };

    // Hard timeout — never hang. Registered users are never bounced to
    // login on a slow network; we keep retrying instead.
    const hardTimeout = setTimeout(() => {
      if (!cancelled && isAuthenticated === null && !isRegisteredUser()) {
        setIsAuthenticated(false);
        toLogin();
      }
    }, 6000);

    const slowTimer = setTimeout(() => { if (!cancelled) setSlow(true); }, 2500);
    let retryTimer;

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 5000,
        });
        if (cancelled) return;
        setIsAuthenticated(true);
        setUser(response.data);
        applyUserSettings();
      } catch (error) {
        if (cancelled) return;
        const status = error.response?.status;
        if (status !== 401 && status !== 403 && isRegisteredUser()) {
          // Network hiccup for a signed-in user — retry, don't sign them out.
          retryTimer = setTimeout(checkAuth, 2000);
          return;
        }
        setIsAuthenticated(false);
        toLogin();
      }
      clearTimeout(hardTimeout);
      clearTimeout(slowTimer);
    };

    checkAuth();
    return () => { cancelled = true; clearTimeout(hardTimeout); clearTimeout(slowTimer); clearTimeout(retryTimer); };
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyUserSettings = async () => {
    // Only apply server settings if this device has no local overrides
    const hasLocalTheme = localStorage.getItem('reveala-theme');
    const hasLocalFont = localStorage.getItem('reveala-font-size');
    const hasLocalFontFamily = localStorage.getItem('reveala-font-family');
    const hasLocalColorMode = localStorage.getItem('eili-color-mode');
    const needsVisualSync = !(hasLocalTheme && hasLocalFont && hasLocalFontFamily && hasLocalColorMode);

    try {
      const res = await axios.get(`${API}/settings`, { withCredentials: true, timeout: 5000 });
      const s = res.data;
      if (needsVisualSync) {
        if (s.color_theme && !hasLocalTheme) changeTheme(s.color_theme);
        if (s.font_size && !hasLocalFont) changeFontSize(s.font_size);
        if (s.font_family && !hasLocalFontFamily) changeFontFamily(s.font_family);
        if (s.color_mode && !hasLocalColorMode) changeColorMode(s.color_mode);
      }
      // Language: mirror the theme/font pattern — only trust the
      // server when the user has NOT explicitly chosen a language on
      // this device. We track explicit choices via the
      // `eili-lang-explicit` marker (set by LanguageSelector and
      // LanguageDetectBanner). Without this guard, the guest-login
      // flow — whose server-side setting defaults to 'en' — would
      // clobber a Spanish speaker's pre-login pick on every reload.
      //
      // Symmetric upward sync: if the user DID make an explicit
      // choice on this device AND it disagrees with the server value,
      // push the client choice back so it becomes durable and shows
      // correctly in Settings the next time the user visits it.
      const userChoseLang = localStorage.getItem('eili-lang-explicit');
      if (s.language && !userChoseLang) {
        i18n.changeLanguage(s.language);
      } else if (userChoseLang && i18n.language && i18n.language !== s.language) {
        axios.patch(`${API}/settings`, { language: i18n.language }, { withCredentials: true })
          .catch(() => { /* offline / unauth — try again next mount */ });
      }
    } catch {
      // Settings not available, use defaults
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-background, #0f1729)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--theme-accent, #6366f1)' }}></div>
          {slow && (
            <p className="animate-pulse" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('common.loading')}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return React.cloneElement(children, { user });
}